import { Area } from "@agp/shared.user-app/entity";
import { Fragment, useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion, Variants } from "motion/react";
import { twMerge } from "tailwind-merge";
import { ExpandableTourCard, DetailedTour } from "../tour/expandable-tour-card";

export type DetailedArea = Area & {
  tours: DetailedTour[];
};

interface ExpandableAreaCardProps {
  area: DetailedArea;
  colorIndex: number;
  blockLabel: string;
  onClickGuide: (id: string) => void;
  currentPlayingGuideId?: string;
  maxAreaNameLines?: number;
}

const listVariants: Variants = {
  visible: {
    transition: {
      staggerChildren: 0.1,
    },
  },
  hidden: {
    transition: {
      duration: 0,
    },
  },
};

const itemVariants: Variants = {
  visible: {
    transition: {
      duration: 0.3,
    },
    opacity: 1,
  },
  hidden: {
    opacity: 0,
  },
};

// エリアカードの色定義
const getAreaColor = (colorIndex: number): string => {
  switch (colorIndex) {
    case 0:
      return "#EA9060"; // オレンジ系
    case 1:
      return "#86B1A8"; // 緑系
    case 2:
      return "#8897C0"; // 青系
    default:
      return "#737373"; // グレー
  }
};

// 展開時の背景色（薄い色）
const getAreaLightColor = (colorIndex: number): string => {
  switch (colorIndex) {
    case 0:
      return "#f5dcc8"; // 薄オレンジ（暖色ピーチ）
    case 1:
      return "#d8dcca"; // 薄緑（暖色セージ/カーキ）
    case 2:
      return "#d2d5e2"; // 薄青（ラベンダー）
    default:
      return "#e8e8e8"; // 薄グレー
  }
};

// 展開時のツアーカード色（ヘッダーより少し濃い同系色）
const getTourCardColor = (colorIndex: number): string => {
  switch (colorIndex) {
    case 0:
      return "#D48850"; // 濃いめオレンジ
    case 1:
      return "#72A498"; // 濃いめグリーン
    case 2:
      return "#7888B2"; // 濃いめブルー
    default:
      return "#666666"; // グレー
  }
};

export const ExpandableAreaCard = (props: ExpandableAreaCardProps) => {
  const { toggle, firstState: isExpanded } = useTwoStepOpenState(10);

  const handleClickExpandButton = useCallback(() => {
    toggle();
  }, [toggle]);

  const areaColor = getAreaColor(props.colorIndex);
  const lightColor = getAreaLightColor(props.colorIndex);
  const tourCardColor = getTourCardColor(props.colorIndex);

  return (
    <>
      {/* ヘッダー: ブロックラベル | ツアー名 | シェブロン */}
      <div
        className={twMerge(
          "relative w-full overflow-hidden text-white flex items-center pr-4 pl-6 py-3 font-bold transition-all duration-300 cursor-pointer",
          isExpanded ? "rounded-t-3xl" : "rounded-3xl"
        )}
        style={{ backgroundColor: areaColor }}
        onClick={handleClickExpandButton}
      >
        {/* ブロックラベル（丸 + 文字） */}
        <div className="flex flex-col items-center shrink-0 mr-8">
          <div
            className="w-16 h-16 rounded-full border-[0px] border-white/80 bg-white/10 flex items-center justify-center"
            style={{ boxShadow: "0 0 12px rgba(255,255,255,0.8)" }}
          >
            <span className="text-white text-4xl font-bold">
              {props.blockLabel}
            </span>
          </div>
          <span className="text-white text-xs mt-1">ブロック</span>
        </div>

        {/* エリア名（改行対応・縦中央揃え） */}
        <div
          className="flex-1 min-w-0 flex items-center"
          style={props.maxAreaNameLines ? { minHeight: `${props.maxAreaNameLines * 1.625}em` } : undefined}
        >
          <span className="text-white text-xl font-medium leading-relaxed">
            {props.area.name.split("\n").map((line, i) => (
              <Fragment key={i}>
                {i > 0 && <br />}
                {line}
              </Fragment>
            ))}
          </span>
        </div>

        {/* トグルアイコン */}
        <img
          alt="expandable-button"
          src="/minpaku-demo/icons/expand-button.svg"
          className={`h-12 w-12 shrink-0 ml-2 transform transition-transform duration-300 ${
            isExpanded ? "rotate-180" : "rotate-0"
          }`}
        />
      </div>

      {/* 展開コンテンツ: ツアーカード一覧 */}
      <div
        className={twMerge(
          "overflow-hidden rounded-b-3xl transition-all duration-300",
          isExpanded ? "h-fit opacity-100" : "h-0 opacity-0"
        )}
        style={{ backgroundColor: lightColor }}
      >
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              variants={listVariants}
              initial={"hidden"}
              animate={"visible"}
              className="py-3 mx-5"
            >
              {props.area.tours.map((tour, tourIndex) => {
                const guideIndexOffset = props.area.tours
                  .slice(0, tourIndex)
                  .reduce((sum, t) => sum + t.guides.length, 0);
                return (
                  <motion.div
                    key={`tour-${tour.id}-${tourIndex}`}
                    variants={itemVariants}
                    className="mb-3"
                  >
                    <ExpandableTourCard
                      tour={tour}
                      onClickGuide={props.onClickGuide}
                      labelColor={tourCardColor}
                      guideItemColor={lightColor}
                      currentPlayingGuideId={props.currentPlayingGuideId}
                      guideIndexOffset={guideIndexOffset}
                    />
                  </motion.div>
                );
              })}
              {props.area.tours.length === 0 && (
                <motion.div
                  variants={itemVariants}
                  className="bg-white p-4 rounded-2xl text-gray-500 text-base text-center"
                >
                  このエリアにはツアーがありません
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export const delay = (milliseconds: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, milliseconds);
  });

const useTwoStepOpenState = (duration: number) => {
  const [firstState, setFirstState] = useState(false);
  const [secondState, setSecondState] = useState(false);

  const toggle = useCallback(() => {
    if (firstState && secondState) {
      setSecondState(false);
    }
    if (!firstState && !secondState) {
      setFirstState(true);
    }
  }, [firstState, secondState]);

  useEffect(() => {
    const propagate = async () => {
      await delay(duration);
      setSecondState(firstState);
    };
    if (firstState) propagate();
  }, [firstState, duration]);

  useEffect(() => {
    const propagate = async () => {
      await delay(duration);
      setFirstState(secondState);
    };
    if (!secondState) propagate();
  }, [secondState, duration]);

  return {
    open: () => setFirstState(true),
    close: () => setSecondState(false),
    toggle,
    firstState,
    secondState,
  };
};
